import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, FileText, Check } from "lucide-react";
import { V } from "@/lib/vocab";

const TRADES = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};

// Manually onboard a vendor the team already trusts — staff fill in everything and the vendor is
// dispatch-ready right away (mode="community") or added to the network directory (mode="network").
export function ManualAddVendor({ mode = "community", tenantId, onDone, onClose }) {
  const [f, setF] = useState({ name: "", contact_name: "", phone: "", email: "", language: "en", market: "" });
  const [trades, setTrades] = useState([]);
  const [coi, setCoi] = useState({ expires_on: "", carrier: "", on_file: false });
  const [coiFile, setCoiFile] = useState(null);
  const [agreementOffline, setAgreementOffline] = useState(false);
  const [agreementFile, setAgreementFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleTrade = (k) => setTrades((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Company name is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({
        name: f.name.trim(), contact_name: f.contact_name.trim(), phone: f.phone.trim(), email: f.email.trim(),
        language: f.language, trades, markets: f.market.trim() ? [f.market.trim()] : [],
        coi: { expires_on: coi.expires_on, carrier: coi.carrier.trim(), on_file: coi.on_file },
        agreement: { signed_offline: agreementOffline },
      }));
      if (coiFile) fd.append("coi_file", coiFile);
      if (agreementFile) fd.append("agreement_file", agreementFile);
      const url = mode === "network"
        ? "/field-services/vendor-directory/manual"
        : `/tenants/${tenantId}/field-services/vendors/manual`;
      const { data } = await api.post(url, fd);
      toast.success(`${data.name} added${data.coi_ok ? " · insured" : ""}`);
      onDone?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, "Couldn't add the vendor")); }
    setBusy(false);
  };

  const input = "w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
  const label = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/45";

  return (
    <div className="space-y-5" data-testid="manual-add-vendor">
      <p className="text-sm leading-relaxed text-ink/60">
        Add a vendor you already work with — you fill in their details and they&rsquo;re ready to go
        {mode === "community" ? ` for this ${V.lc("org")}` : " on the network"} right away. Add crew afterward from the vendor card.
      </p>

      <div className="space-y-3">
        <div>
          <label className={label}>Company name</label>
          <input data-testid="manual-vendor-name" className={input} value={f.name}
            onChange={(e) => set("name", e.target.value)} placeholder="e.g. Cedar Creek Plumbing" />
        </div>
        <div>
          <label className={label}>Trades</label>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(TRADES).map(([k, l]) => (
              <button key={k} type="button" data-testid={`manual-vendor-trade-${k}`} onClick={() => toggleTrade(k)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${trades.includes(k) ? "border-redline-500 bg-redline-50 text-redline-700" : "border-sand-300 text-ink/60 hover:border-sand-400"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label}>Service area <span className="normal-case text-ink/30">(optional)</span></label>
            <input data-testid="manual-vendor-market" className={input} value={f.market}
              onChange={(e) => set("market", e.target.value)} placeholder="e.g. Austin, TX" />
          </div>
          <div>
            <label className={label}>Language</label>
            <select data-testid="manual-vendor-language" className={input} value={f.language}
              onChange={(e) => set("language", e.target.value)}>
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-sand-200 bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Primary contact (dispatcher)</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="manual-vendor-contact-name" className={input} value={f.contact_name}
            onChange={(e) => set("contact_name", e.target.value)} placeholder="Contact name" />
          <input data-testid="manual-vendor-phone" className={input} value={f.phone}
            onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number" />
        </div>
        <input data-testid="manual-vendor-email" className={input} value={f.email}
          onChange={(e) => set("email", e.target.value)} placeholder="Email" />
        <p className="text-xs text-ink/40">
          We won&rsquo;t text or email them automatically — they opt in before any job texts go out.
        </p>
      </div>

      <div className="space-y-3 rounded-2xl border border-sand-200 bg-white p-4">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">
          <ShieldCheck className="h-3.5 w-3.5" /> Insurance (COI)
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label}>Expiry date</label>
            <input type="date" data-testid="manual-vendor-coi-expiry" className={input} value={coi.expires_on}
              onChange={(e) => setCoi((c) => ({ ...c, expires_on: e.target.value }))} />
          </div>
          <div>
            <label className={label}>Carrier <span className="normal-case text-ink/30">(optional)</span></label>
            <input data-testid="manual-vendor-coi-carrier" className={input} value={coi.carrier}
              onChange={(e) => setCoi((c) => ({ ...c, carrier: e.target.value }))} placeholder="e.g. State Farm" />
          </div>
        </div>
        <input type="file" accept=".pdf,image/*" data-testid="manual-vendor-coi-file"
          onChange={(e) => setCoiFile(e.target.files?.[0] || null)} className="block text-xs text-ink/70" />
        <label className="flex items-center gap-2 text-sm text-ink/70">
          <input type="checkbox" data-testid="manual-vendor-coi-onfile" checked={coi.on_file}
            onChange={(e) => setCoi((c) => ({ ...c, on_file: e.target.checked }))} />
          It&rsquo;s on file elsewhere — don&rsquo;t upload now
        </label>
        <p className="text-xs text-ink/40">
          Upload the certificate (with an expiry) to mark them insured. Otherwise they&rsquo;re still added
          dispatch-ready, with a &ldquo;no insurance&rdquo; flag you can clear later.
        </p>
      </div>

      <div className="space-y-3 rounded-2xl border border-sand-200 bg-white p-4">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">
          <FileText className="h-3.5 w-3.5" /> Vendor agreement
        </div>
        <input type="file" accept=".pdf,image/*" data-testid="manual-vendor-agreement-file"
          onChange={(e) => setAgreementFile(e.target.files?.[0] || null)} className="block text-xs text-ink/70" />
        <label className="flex items-center gap-2 text-sm text-ink/70">
          <input type="checkbox" data-testid="manual-vendor-agreement-offline" checked={agreementOffline}
            onChange={(e) => setAgreementOffline(e.target.checked)} />
          Signed offline / not required
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <button data-testid="manual-vendor-cancel" onClick={onClose}
          className="rounded-xl px-4 py-2.5 text-sm font-semibold text-ink/60 hover:text-ink">Cancel</button>
        <button data-testid="manual-vendor-submit" onClick={submit} disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-redline-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Add vendor
        </button>
      </div>
    </div>
  );
}
